import React from 'react';
import { Helmet } from 'react-helmet-async';

// Chrome Extension Privacy Policy Component
export const ChromeExtensionPrivacy: React.FC = () => {
  // Get current date in format Month Day, Year
  const formattedDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Helmet>
        <title>Privacy Policy for IPTVPlayer.Stream Chrome Extension</title>
        <meta name="description" content="Privacy Policy for the IPTVPlayer.Stream Chrome Extension" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        {/* Header with accent color */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 py-8 px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Privacy Policy
          </h1>
          <p className="text-blue-100">
            IPTVPlayer.Stream Chrome Extension
          </p>
          <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full bg-white/20 text-sm text-white">
            Last Updated: {formattedDate}
          </div>
        </div>

        {/* Content with improved spacing */}
        <div className="px-8 py-8 prose dark:prose-invert max-w-none">
          {/* Introduction with card styling */}
          <div className="mb-8 p-6 bg-blue-50 dark:bg-gray-700 rounded-lg border-l-4 border-blue-500">
            <h2 className="text-2xl font-semibold text-blue-700 dark:text-blue-300 mt-0">Introduction</h2>
            <p className="mb-0">
              This Privacy Policy explains how the IPTVPlayer.Stream Chrome Extension ("we", "our", or "extension") 
              handles your information when you use our extension. We are committed to protecting your privacy and 
              ensuring you have a positive experience using our extension.
            </p>
          </div>

          {/* Information Collection section */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 border-b pb-2 border-gray-200 dark:border-gray-700">
              We don't Collect your Data
            </h2>
            
            <div className="mt-6">
              <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 flex items-center">
                <span className="inline-block w-10 h-10 flex-shrink-0 bg-blue-100 dark:bg-blue-900 rounded-full mr-3 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 dark:text-blue-300" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                  </svg>
                </span>
                Local Storage
              </h3>
              <p className="my-3 ml-13 text-gray-700 dark:text-gray-300">
                The extension stores the following information locally on your device:
              </p>
              <ul className="ml-13 space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="font-medium">Recent Stream URLs</strong>: We store up to 5 recently used stream URLs to provide quick access to your frequently used streams. 
                    This data never leaves your device.
                  </div>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="font-medium">Extension Settings</strong>: Basic settings such as whether you've seen the welcome message.
                  </div>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="font-medium">Playback Settings</strong>: Settings like volume level, mute status, and playback speed preferences for individual streams.
                  </div>
                </li>
              </ul>
            </div>

            <div className="mt-6">
              <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 flex items-center">
                <span className="inline-block w-10 h-10 flex-shrink-0 bg-blue-100 dark:bg-blue-900 rounded-full mr-3 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 dark:text-blue-300" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                  </svg>
                </span>
                Integrated Player Usage
              </h3>
              <p className="my-3 ml-13 text-gray-700 dark:text-gray-300">
                When you click the "Play" button, the stream is played directly in our integrated player within the extension. 
                We do not:
              </p>
              <ul className="ml-13 space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Redirect you to external websites
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Share your stream URLs with any third parties
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Track your viewing habits or content choices
                </li>
              </ul>
            </div>
          </section>

          {/* How We Use section */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 border-b pb-2 border-gray-200 dark:border-gray-700">
              How We Use Information
            </h2>
            <ul className="mt-4 space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex items-start p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <strong className="font-medium">Local Data</strong>: Used solely to enhance your experience with the extension by remembering your preferences and recent streams.
                </div>
              </li>
              <li className="flex items-start p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <strong className="font-medium">Stream URLs</strong>: Only used locally to play your requested content in the integrated player.
                </div>
              </li>
            </ul>
          </section>

          {/* Data Sharing section */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 border-b pb-2 border-gray-200 dark:border-gray-700">
              Data Sharing
            </h2>
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border-l-4 border-green-500 text-gray-700 dark:text-gray-300">
              <p className="mb-0 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <strong className="font-medium">We do not share any of your data with third parties.</strong>
              </p>
              <p className="mt-2 ml-8">
                The stream URLs you enter remain within the extension and are only used for playback in the integrated player.
              </p>
            </div>
          </section>

          {/* Your Choices section */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 border-b pb-2 border-gray-200 dark:border-gray-700">
              Your Choices
            </h2>
            <p className="mt-4 text-gray-700 dark:text-gray-300">
              You can clear your recent streams history at any time by:
            </p>
            <ol className="mt-3 space-y-3 text-gray-700 dark:text-gray-300">
              <li className="flex items-center">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center font-semibold text-blue-600 dark:text-blue-300 mr-3">1</span>
                Right-clicking on the extension icon
              </li>
              <li className="flex items-center">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center font-semibold text-blue-600 dark:text-blue-300 mr-3">2</span>
                Selecting "Options"
              </li>
              <li className="flex items-center">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center font-semibold text-blue-600 dark:text-blue-300 mr-3">3</span>
                Clicking "Clear History"
              </li>
            </ol>
          </section>

          {/* Security section */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 border-b pb-2 border-gray-200 dark:border-gray-700">
              Security
            </h2>
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-gray-700 dark:text-gray-300 flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p className="mb-0">
                We implement appropriate security measures to protect your information. Since all data is stored locally on your device, 
                it is protected by your browser's security mechanisms.
              </p>
            </div>
          </section>

          {/* Changes to Policy section */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 border-b pb-2 border-gray-200 dark:border-gray-700">
              Changes to This Policy
            </h2>
            <p className="mt-4 text-gray-700 dark:text-gray-300">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy 
              on our website and updating the extension.
            </p>
          </section>

          {/* Contact Us section */}
          <section className="mt-8 p-6 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Contact Us
            </h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <a 
              href="mailto:support@mail.iptvplayer.stream" 
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="mr-2 -ml-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              support@mail.iptvplayer.stream
            </a>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ChromeExtensionPrivacy; 